import { useEffect, useRef, useState } from 'react';
import { Box, FormControl, Stack } from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';

import { Button, FormLabel } from '@/components/v4';
import PictureViewer from 'components/pictureViewer/PictureViewer';
import FieldError from 'components/forms/new/FieldError';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import useFileUpload from 'hooks/useFileUpload';
import useDetectFileType from 'hooks/useDetectFileType';
import {
    formLabelStyling,
    stackSpacing,
    stackStyling,
} from 'components/forms/new/formStyles';

export const IMAGE_TYPE_ERROR = 'Invalid file format. Allowed types .png .svg';

/**
 * Service Category image field. Mirrors TemplateImageField's
 * useFileUpload + useDetectFileType + Button-as-label pattern, but accepts
 * both .png and .svg (the old page's AddServiceCategory.jsx/EditGeneral.jsx
 * allowed both, whereas Templates only allowed .png).
 *
 * @param {object}   params
 * @param {boolean}  params.darkMode      Current dark mode state.
 * @param {string}   params.existingImage The stored image filename for an existing service category (edit mode).
 * @param {function} params.setFieldValue Formik's setFieldValue, used to set `image` on selection.
 * @param {string}   [params.error]       Manual validation error (e.g. "Image is required" on create).
 */
const ServiceCategoryImageField = ({
    darkMode,
    existingImage,
    setFieldValue,
    error,
}) => {
    const fileRef = useRef(null);
    const [typeError, setTypeError] = useState(null);
    const [isHovered, setIsHovered] = useState(false);

    const { fileReaderResult, uploadHandler, file } = useFileUpload({
        allowedExtensions: ['.png', '.svg'],
        fileRef,
    });

    useEffect(() => {
        if (fileReaderResult && file) {
            setFieldValue('image', file);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileReaderResult]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        const extension = selectedFile?.name?.split('.').pop()?.toLowerCase();
        if (selectedFile && extension !== 'png' && extension !== 'svg') {
            setTypeError(IMAGE_TYPE_ERROR);
            e.target.value = '';
            return;
        }
        setTypeError(null);
        uploadHandler(e);
    };

    const previewSrc =
        fileReaderResult ||
        (existingImage
            ? `${import.meta.env.VITE_APP_IMAGE_BASE}/images/${existingImage}`
            : null);

    const previewFileType = useDetectFileType(previewSrc);

    const displayError = typeError || error;

    return (
        <FormControl
            isRequired
            isInvalid={!!displayError}
            sx={{ margin: '0 !important' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack direction="row" spacing={stackSpacing} sx={stackStyling()}>
                <FormLabel sx={formLabelStyling(darkMode)}>Image</FormLabel>
                <Box
                    sx={{
                        display: 'flex',
                        width: '100%',
                        alignItems: 'flex-start',
                        paddingTop: '15px',
                    }}>
                    <Button
                        as="label"
                        size="sm"
                        sx={{ marginY: '5px', cursor: 'pointer' }}>
                        <AttachmentIcon mr={2} />
                        {file ? file.name : 'Choose Image'}
                        <input
                            style={{ display: 'none' }}
                            type="file"
                            accept=".png,.svg"
                            onChange={handleFileChange}
                            ref={fileRef}
                        />
                    </Button>
                    {isHovered && (
                        <FieldToolTip
                            label="Icon shown for this category on service profile and vendor defined service listings. PNG or SVG."
                            ariaLabel="service-category-image-tip"
                            left="25px"
                            top="10px"
                        />
                    )}
                </Box>
            </Stack>
            <FieldError isDrawer={false}>{displayError}</FieldError>
            {(previewFileType === 'png' || previewFileType === 'svg') && (
                <PictureViewer imageURL={previewSrc} />
            )}
        </FormControl>
    );
};

export default ServiceCategoryImageField;
